<script setup lang="ts">
  // Baked theme.css must not be imported here — it paints `--v0-*` on `:root`.
  import { EmeraldStyleSheetAdapter, emeraldColors, emeraldDarkColors } from '@paper/emerald'
  import { useHead } from '@unhead/vue'
  import faqs from 'virtual:faqs'
  import mdRoutes from 'virtual:md-routes'
  import pageDates from 'virtual:page-dates'

  // Framework
  import { IN_BROWSER, Scrim, useBreakpoints, useStack } from '@vuetify/v0'

  // Components
  import AppMeshBg from '@/components/app/AppMeshBg.vue'

  // Composables
  import { useAsk } from '@/composables/useAsk'
  import { useBreadcrumbItems } from '@/composables/useBreadcrumbItems'
  import { createLevelFilter } from '@/composables/useLevelFilter'
  import { createNavConfig } from '@/composables/useNavConfig'
  import { useScrollLock } from '@/composables/useScrollLock'
  import { useScrollPersist } from '@/composables/useScrollPersist'
  import { useSearch } from '@/composables/useSearch'
  import { useSettings } from '@/composables/useSettings'

  // Constants
  import { INDEXABLE, PROD_SITE_URL } from '@/constants/site'

  // Stores
  import { useAppStore } from '@/stores/app'

  // Utilities
  import { defineAsyncComponent, toRef, watch } from 'vue'
  import { useRoute } from 'vue-router'

  const emeraldAdapter = new EmeraldStyleSheetAdapter({
    v0Aliases: true,
    stylesheetId: 'emerald-docs-tokens',
  })

  const AppSettingsSheet = defineAsyncComponent(() => import('@/components/app/AppSettingsSheet.vue'))
  const DocsSearch = defineAsyncComponent(() => import('@/components/docs/DocsSearch.vue'))

  useScrollPersist()
  const settings = useSettings()
  const route = useRoute()

  watch(() => route.fullPath, (to, from) => {
    if (!IN_BROWSER) return
    if (to.includes('#') || history.state?.scroll) return
    if (to === from) return
    window.scrollTo({ top: 0, behavior: settings.prefersReducedMotion.value ? 'auto' : 'smooth' })
  })

  // Provider plumbing — lifted from layouts
  const app = useAppStore()
  const levelFilter = createLevelFilter(() => app.nav)
  levelFilter.provide()

  const navConfig = createNavConfig(levelFilter.filteredNav)
  navConfig.provide()

  // Modals & global state
  const ask = useAsk()
  const search = useSearch()
  const stack = useStack()
  const breakpoints = useBreakpoints()

  // Unified body scroll lock — replaces the three per-layout impls
  useScrollLock(() => stack.isActive.value)

  const slideTransition = toRef(() => settings.prefersReducedMotion.value ? undefined : 'slide')

  const isModalOpen = toRef(() => {
    if (search.isOpen.value) return true
    if (settings.isOpen.value) return true
    if (ask.isOpen.value && !breakpoints.lgAndUp.value) return true
    return false
  })

  const url = toRef(() => `${PROD_SITE_URL}${route.path}`)
  const breadcrumbs = useBreadcrumbItems()

  // Advertise the page's markdown twin so agent fetchers and AI crawlers can
  // retrieve source markdown instead of scraping rendered HTML. Only emitted for
  // routes that actually have a twin — see build/md-routes.ts.
  const markdown = toRef(() => {
    const twin = mdRoutes[route.path] ?? mdRoutes[route.path.replace(/\/$/, '')]
    if (!twin) return []

    return [{
      key: 'alternate-markdown',
      rel: 'alternate',
      type: 'text/markdown',
      href: `${PROD_SITE_URL}${twin}`,
    }]
  })

  const breadcrumbScript = toRef(() => {
    if (route.path === '/') return []

    const items = breadcrumbs.value
    if (items.length <= 1) return []

    return [{
      key: 'breadcrumb-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items.map((item, index) => {
          const isLast = index === items.length - 1
          const name = index === 0 ? 'Vuetify0' : item.text
          const entry: Record<string, unknown> = {
            '@type': 'ListItem',
            'position': index + 1,
            name,
          }
          if (!isLast && item.to) entry.item = `${PROD_SITE_URL}${item.to}`
          return entry
        }),
      }),
    }]
  })

  // Documentation pages are TechArticle, not bare WebSite nodes — it carries the
  // headline and dateModified that AI answer engines use to judge freshness.
  // `dateModified` is the page's last git commit, already collected for the
  // freshness badge.
  const articleScript = toRef(() => {
    const items = breadcrumbs.value
    if (items.length <= 1) return []

    const headline = items.at(-1)?.text
    if (!headline) return []

    const dates = pageDates[route.path]

    return [{
      key: 'article-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        headline,
        'url': url.value,
        'isPartOf': { '@type': 'WebSite', 'name': 'Vuetify0', 'url': PROD_SITE_URL },
        'about': { '@type': 'SoftwareSourceCode', 'name': '@vuetify/v0', 'programmingLanguage': 'TypeScript' },
        'publisher': { '@type': 'Organization', 'name': 'Vuetify', 'url': 'https://vuetifyjs.com' },
        ...dates?.updated ? { dateModified: dates.updated } : {},
      }),
    }]
  })

  // FAQPage markup must mirror content the reader can see, so these come from
  // the same `::: faq` blocks the page renders — see build/generate-faqs.ts.
  const faqScript = toRef(() => {
    const items = faqs[route.path] ?? faqs[route.path.replace(/\/$/, '')]
    if (!items?.length) return []

    return [{
      key: 'faq-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': items.map(item => ({
          '@type': 'Question',
          'name': item.question,
          'acceptedAnswer': { '@type': 'Answer', 'text': item.answer },
        })),
      }),
    }]
  })

  useHead({
    title: 'Vuetify0',
    titleTemplate: '%s — Vuetify0',
    link: toRef(() => [
      { rel: 'preconnect', href: 'https://api.github.com' },
      { rel: 'preconnect', href: 'https://cdn.vuetifyjs.com' },
      { rel: 'dns-prefetch', href: 'https://api.npmjs.org' },
      { key: 'canonical', rel: 'canonical', href: url.value },
      // Site-wide LLM context bundles. Documented for humans on
      // /guide/tooling/ai-tools; these make them machine-discoverable.
      { key: 'llms', rel: 'alternate', type: 'text/plain', href: `${PROD_SITE_URL}/llms.txt`, title: 'llms.txt' },
      { key: 'llms-full', rel: 'alternate', type: 'text/plain', href: `${PROD_SITE_URL}/llms-full.txt`, title: 'llms-full.txt' },
      ...markdown.value,
    ]),
    meta: [
      { key: 'description', name: 'description', content: 'Headless components and composables for building modern applications and design systems' },
      { key: 'og:type', property: 'og:type', content: 'website' },
      { key: 'og:site_name', property: 'og:site_name', content: 'Vuetify0' },
      { key: 'og:locale', property: 'og:locale', content: 'en_US' },
      { key: 'og:url', property: 'og:url', content: url },
      { key: 'og:image', property: 'og:image', content: 'https://cdn.vuetifyjs.com/docs/images/one/logos/vzero-logo-og.png' },
      { key: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
      { key: 'twitter:site', name: 'twitter:site', content: '@VuetifyJS' },
      ...INDEXABLE ? [] : [{ key: 'robots', name: 'robots', content: 'noindex, nofollow' }],
    ],
    style: [{
      key: 'emerald-docs-tokens',
      id: 'emerald-docs-tokens',
      innerHTML: emeraldAdapter.generate({
        'emerald-light': emeraldColors,
        'emerald-dark': emeraldDarkColors,
      }),
    }],
    script: toRef(() => [
      {
        key: 'website-schema',
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          'name': 'Vuetify0',
          'url': PROD_SITE_URL,
          'description': 'Headless components and composables for building modern applications and design systems',
          'publisher': {
            '@type': 'Organization',
            'name': 'Vuetify',
            'url': 'https://vuetifyjs.com',
            'logo': 'https://cdn.vuetifyjs.com/docs/images/one/logos/vzero-logo-og.png',
          },
        }),
      },
      ...breadcrumbScript.value,
      ...articleScript.value,
      ...faqScript.value,
    ]),
  })
</script>

<template>
  <AppMeshBg />

  <div
    class="app-shell min-h-screen text-on-background"
    :class="{ 'dot-grid': settings.showDotGrid.value }"
    :data-code-size="settings.codeSize.value"
    :data-reduce-motion="settings.prefersReducedMotion.value || undefined"
    :style="{ '--line-opacity': `${settings.dotGridIntensity.value}%`, '--dot-coverage': `${settings.dotGridCoverage.value}%` }"
  >
    <a
      class="fixed top-2 start-2 z-50 px-4 py-2 bg-primary text-on-primary rounded -translate-y-[200%] focus:translate-y-0"
      href="#main-content"
    >
      Skip to main content
    </a>

    <div id="docs-copy-status" aria-live="polite" class="sr-only" role="status" />

    <div class="app-shell-content pt-[calc(48px+var(--app-banner-h,0px))]">
      <AppBanner />
      <AppBar />

      <div :inert="isModalOpen || undefined">
        <router-view />
      </div>

      <Scrim class="fixed inset-0 bg-black/30 transition-opacity" :teleport="false" />
    </div>

    <DocsSearch />

    <Transition :name="slideTransition">
      <AppSettingsSheet v-if="settings.isOpen.value" />
    </Transition>

    <DocsApiHover />
    <DocsHighlight />
    <DocsTextLink />
  </div>
</template>

<style>
  html {
    scroll-padding-top: calc(48px + var(--app-banner-h, 0px) + 0.5rem);
  }

  #app > .app-shell {
    position: relative;
    background: color-mix(in srgb, var(--v0-background) 85%, transparent);

    /*
     * App-shell dot grid (toggled via Settings → "Dot grid pattern").
     *
     * Intentionally a separate inline implementation from the AppDotGrid
     * component: this is a full-viewport shell background with a diagonal fade
     * and a user toggle, whereas AppDotGrid is a per-section accent with a
     * radial fade from a corner — different roles, so neither is forced onto
     * the other. The dot weight, 20px density, and connecting-line construction
     * (lines offset half a cell so the dots sit on the intersections) are kept
     * deliberately in sync with the GnDotGrid primitive in @paper/genesis;
     * change the look there and mirror it here.
     */
    &.dot-grid::before {
      --dot-opacity: 12%;
      /* --line-opacity is set inline from the "Line intensity" setting (defaults to 0.85%). */
      /* --dot-coverage is set inline from the "Dot coverage" setting (defaults to 15%): the
         diagonal fade stays solid to that stop, then ramps to transparent 20% further out. */
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 100vh;
      z-index: 0;
      pointer-events: none;
      background:
        radial-gradient(circle, color-mix(in srgb, var(--v0-on-background) var(--dot-opacity), transparent) 1px, transparent 1px),
        linear-gradient(to right, color-mix(in srgb, var(--v0-on-background) var(--line-opacity, 0.85%), transparent) 1px, transparent 1px),
        linear-gradient(to bottom, color-mix(in srgb, var(--v0-on-background) var(--line-opacity, 0.85%), transparent) 1px, transparent 1px);
      background-size: 20px 20px;
      background-position: 0 0, 10px 10px, 10px 10px;
      mask-image: linear-gradient(
        225deg,
        black 0%,
        black var(--dot-coverage, 15%),
        transparent calc(var(--dot-coverage, 15%) + 20%)
      );
      -webkit-mask-image: linear-gradient(
        225deg,
        black 0%,
        black var(--dot-coverage, 15%),
        transparent calc(var(--dot-coverage, 15%) + 20%)
      );
    }

    [data-theme]:not([data-theme="light"]):not([data-theme="odyssey"]):not([data-theme="tailwind-light"]):not([data-theme="material-3-light"]):not([data-theme="ant-design-light"]):not([data-theme="radix-light"]):not([data-theme="emerald-light"]):not([data-theme="bulma-light"]) &.dot-grid::before {
      --dot-opacity: 10%;
    }

    &.dot-grid > .app-shell-content {
      position: relative;
      z-index: 1;
    }

    hr {
      border: none;
      border-top: 1px solid var(--v0-divider);
    }
  }

  .border {
    border-color: var(--v0-divider);
  }

  .markdown-body {
    .v0-link {
      color: var(--v0-primary);
      transition: color 0.2s;

      &:hover {
        text-decoration: underline;
      }
    }

    h1, h2, h3, h4, h5, h6 {
      position: relative;

      /* Long API tokens (useIntersectionObserver, SUPPORTS_*) must break
         instead of widening the layout viewport on phones. */
      @media (max-width: 767px) {
        overflow-wrap: anywhere;
      }

      > .header-anchor {
        color: inherit;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        align-self: center;
        /* mdiCheck. A pseudo-element cannot mount AppIcon. */
        --header-check: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M21 7 9 19 3.5 13.5 4.91 12.09 9 16.17 19.59 5.59 21 7Z'/%3E%3C/svg%3E");

        &::before,
        &::after {
          content: '#';
          color: var(--v0-primary);
          transition: opacity 0.2s;
        }

        &.header-anchor--copied::before,
        &.header-anchor--copied::after {
          content: '';
          width: 1em;
          height: 1lh;
          background-color: var(--v0-success);
          opacity: 1;
          -webkit-mask: var(--header-check) center / 1em 1em no-repeat;
          mask: var(--header-check) center / 1em 1em no-repeat;
        }

        &.header-anchor--copied::before {
          left: -1.15em;
          top: 0;
        }

        &::before {
          position: absolute;
          left: -.75em;
          opacity: 0;
        }

        &::after {
          /* Out of flow — an in-flow '#' extends the widest line by ~1em
             and with it the page's scrollWidth on mobile. */
          position: absolute;
          margin-inline-start: 0.25em;
          opacity: 0;
        }

        @media (min-width: 768px) {
          &::after {
            display: none;
          }
        }

        @media (max-width: 767px) {
          &::before {
            display: none;
          }

          &::after {
            /* Explicit offsets, heading-relative: the static position of an
               abspos child of the inline-flex anchor is the flex alignment
               origin — over the heading's first characters — and anything
               hung past the text end re-widens wrapped headings. Pinned to
               the inline-end of the heading's last line instead: a wrapped
               heading's first line is full by construction, its last line is
               the short leftover. */
            inset-inline-end: 0;
            bottom: 0;
          }
        }
      }

      &:hover > .header-anchor::before,
      &:hover > .header-anchor::after,
      &:target > .header-anchor::after {
        opacity: 1;
      }
    }

    > h1 {
      font-size: 2.25rem;
      line-height: 2.5rem;
      margin-bottom: 1rem;
    }

    > h2 {
      font-size: 1.875rem;
      line-height: 2.25rem;
      margin-top: 2rem;
      margin-bottom: 0.75rem;
    }

    > h3 {
      font-size: 1.5rem;
      line-height: 2rem;
      margin-top: 1.5rem;
      margin-bottom: 0.5rem;
    }

    > :is(h1,h2,h3) + :is(h2,h3) {
      margin-top: 0;
    }

    blockquote {
      margin: 1rem 0;
      padding: 0.5rem 1rem;
      background-color: var(--v0-surface);
      border-left: 4px solid var(--v0-divider);
    }

    code {
      font-family: 'Courier New', Courier, monospace;

      /* Inline paths/imports in prose wrap instead of widening the page;
         inert inside pre-formatted (.shiki) blocks. */
      @media (max-width: 767px) {
        overflow-wrap: anywhere;
      }
    }

    p {
      margin-bottom: .5rem;
    }

    > ul, > ol {
      list-style-type: disc;
      padding-left: 1.5rem;
    }

    > ul:not(:last-child),
    > ol:not(:last-child) {
      margin-bottom: 1rem;
    }

    > ul > li:not(:last-child),
    > ol > li:not(:last-child) {
      margin-bottom: 0.5rem;
    }

    .shiki {
      overflow: hidden;
    }

    .shiki code {
      display: block;
      overflow-x: auto;
      padding: 0.5rem 1rem;
    }

    /* Prose tables only. Self-styled grid examples opt out via [data-grid] on
       their wrapper — otherwise this rule's specificity (0,1,1) overrides their
       own utility classes (0,1,0) and double-borders the table. Bare example
       tables (no wrapper) still get this default styling. */
    table:not([data-grid] table) {
      width: 100%;
      background-color: var(--v0-surface);
      border-collapse: separate;
      border-spacing: 0;
      border-radius: 0.5rem;
      border: thin solid var(--v0-divider);
      overflow: hidden;

      th, td {
        padding: 0.5rem 0.75rem;
        border-bottom: thin solid var(--v0-divider);
        border-right: thin solid var(--v0-divider);
      }

      th {
        background-color: var(--v0-surface-tint);
        font-weight: 600;
      }

      th:last-child, td:last-child {
        border-right: none;
      }

      tr:last-child td {
        border-bottom: none;
      }
    }

    /* Markdown-emitted tables only — .docs-table is the wrapper both
       markdown pipelines emit (build/markdown.ts, useMarkdown.ts). Example
       and app tables (data-grid, benchmarks, freshness) manage their own
       widths and must not be clamped. */
    @media (max-width: 767px) {
      /* width:100% resolves to max(container, min-content) at phone widths
         and squeezes every wrappable column to one word per line. Let the
         table take its natural width (capped so prose cells still wrap) and
         scroll inside its wrapper. Both wrapper classes, so this outranks
         the base table rule structurally — (0,3,2) over its (0,2,2) —
         instead of by source order. */
      div.docs-table.overflow-x-auto > table {
        width: max-content;
        min-width: 100%;
        max-width: 42rem;
      }

      /* Scroll affordance: overlay scrollbars are invisible until touched,
         leaving no signal that a wide table extends past the viewport. Fade
         the clipped edge of an overflowing wrapper — the fade tracks scroll
         position and vanishes at the reached edge (an inactive timeline
         leaves the no-fade base values, so non-overflowing tables are
         untouched). Browsers without scroll timelines keep a thin
         scrollbar. */
      div.docs-table {
        scrollbar-width: thin;
        scrollbar-color: color-mix(in srgb, var(--v0-on-surface) 40%, transparent) transparent;
        padding-bottom: 2px;
      }

      @supports (animation-timeline: scroll(self x)) {
        div.docs-table {
          --table-fade-start: 0px;
          --table-fade-end: 0px;
          mask-image: linear-gradient(to right, transparent 0, black var(--table-fade-start), black calc(100% - var(--table-fade-end)), transparent 100%);
          -webkit-mask-image: linear-gradient(to right, transparent 0, black var(--table-fade-start), black calc(100% - var(--table-fade-end)), transparent 100%);
          animation: docs-table-edge-fade linear both;
          animation-timeline: scroll(self x);

          /* RTL scrolls from the right edge, so the fade sides flip. */
          [dir='rtl'] & {
            mask-image: linear-gradient(to left, transparent 0, black var(--table-fade-start), black calc(100% - var(--table-fade-end)), transparent 100%);
            -webkit-mask-image: linear-gradient(to left, transparent 0, black var(--table-fade-start), black calc(100% - var(--table-fade-end)), transparent 100%);
          }
        }
      }
    }
  }

  /* Registered so the table-wrapper edge fade interpolates smoothly instead
     of flipping mid-scroll (custom properties animate discretely otherwise). */
  @property --table-fade-start {
    syntax: '<length>';
    inherits: false;
    initial-value: 0px;
  }

  @property --table-fade-end {
    syntax: '<length>';
    inherits: false;
    initial-value: 0px;
  }

  @keyframes docs-table-edge-fade {
    0% {
      --table-fade-start: 0px;
      --table-fade-end: 2.5rem;
    }

    100% {
      --table-fade-start: 2.5rem;
      --table-fade-end: 0px;
    }
  }

  /* Markdown footnotes (markdown-it-footnote) */
  .footnote-ref {
    font-size: 0.75em;
    line-height: 0;
    margin-left: 0.125rem;
  }

  .footnote-ref a,
  .footnote-ref a.v0-link {
    color: var(--v0-primary);
    text-decoration: none;
  }

  .footnote-ref a:hover {
    text-decoration: underline;
  }

  .footnotes-sep {
    margin-top: 3rem;
    border: none;
    border-top: 1px solid var(--v0-divider);
  }

  .footnotes {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: var(--v0-on-surface-variant);
  }

  .footnotes-list {
    list-style: decimal;
    padding-left: 1.5rem;
  }

  .footnotes-list li {
    margin-bottom: 0.25rem;
  }

  .footnotes-list li :is(p, ul, ol) {
    margin: 0;
  }

  .footnote-backref,
  .footnote-backref.v0-link {
    margin-left: 0.25rem;
    color: var(--v0-primary);
    text-decoration: none;
    font-size: 0.875em;
  }

  .footnote-backref:hover {
    text-decoration: underline;
  }

  /* Code size — one variable drives every shiki surface (fences, code
     groups, API cards, example panes). The app-shell data attribute is
     bound to the codeSize setting. */
  .app-shell {
    --docs-code-size: 0.8125rem;
    /* Long example files scroll inside this cap. Shorter files never reach it. */
    --docs-code-window-max: min(56rem, 105vh);
  }

  .app-shell[data-code-size='medium'] {
    --docs-code-size: 0.875rem;
  }

  .app-shell[data-code-size='large'] {
    --docs-code-size: 1rem;
  }

  .shiki code {
    font-size: var(--docs-code-size);
  }

  /* Fence expand, the example-code peek, and those two chevrons. The OS
     media query is not the preference: settings resolves system detection,
     the explicit on/off choice, and the tour override. The Uno preflight
     pins every duration to 0.01ms !important under prefers-reduced-motion,
     so an explicit Full motion choice has to outrank it here only.
     FAQ and description peeks stay on the preflight. */
  .app-shell[data-reduce-motion] .docs-markup__window,
  .app-shell[data-reduce-motion] .genesis-docs-example-code__content,
  .app-shell[data-reduce-motion] .docs-markup ~ .genesis-peek .genesis-peek__chevron,
  .app-shell[data-reduce-motion] .genesis-docs-example > .genesis-peek .genesis-peek__chevron {
    transition: none;
  }

  .app-shell:not([data-reduce-motion]) .docs-markup__window,
  .app-shell:not([data-reduce-motion]) .genesis-docs-example-code__content {
    transition-duration: 0.3s !important;
  }

  .app-shell:not([data-reduce-motion]) .docs-markup ~ .genesis-peek .genesis-peek__chevron,
  .app-shell:not([data-reduce-motion]) .genesis-docs-example > .genesis-peek .genesis-peek__chevron {
    transition-duration: 0.15s !important;
  }

  /* DocsMarkup code block styling */
  .docs-markup .shiki {
    padding-top: 2rem;
  }

  .docs-markup .shiki code {
    padding-bottom: 1rem;
  }

  @media (max-width: 768px) {
    .docs-markup .shiki code {
      padding-right: 5rem;
    }
  }

  .docs-markup--wrap .shiki code {
    white-space: pre-wrap;
    word-break: break-word;
  }

  /* DocsCodeGroup code block styling */
  .docs-code-group .shiki {
    border-top: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  /* DocsApiCard code block styling */
  .docs-api-card .shiki {
    border: none;
    border-radius: 0;
    margin: 0;
    padding-top: 2rem;
  }

  .docs-api-card .shiki code {
    padding: 1rem;
    padding-right: 5rem;
    line-height: 1.625;
  }

  .docs-api-card--wrap .shiki code {
    white-space: pre-wrap;
    word-break: break-word;
  }

  /* DocsReleases code block styling */
  .docs-releases pre {
    background-color: var(--v0-pre);
    padding: 1rem;
    border-radius: 0.25rem;
    overflow-x: auto;
    margin: 0.75rem 0;
  }

  .docs-releases code {
    background-color: var(--v0-surface-tint);
    padding: 0 0.25rem;
    border-radius: 0.25rem;
  }

  .docs-releases pre code {
    background-color: transparent;
    padding: 0;
  }

  /* Shiki theme switching — override inline vars to use theme surface color */
  .shiki {
    --shiki-light-bg: var(--v0-surface, #fff) !important;
    --shiki-dark-bg: var(--v0-surface, #1a1a1a) !important;
    --shiki-light: var(--v0-on-surface) !important;
    --shiki-dark: var(--v0-on-surface) !important;
    background-color: var(--v0-surface);
    color: var(--v0-on-surface);
    border: thin solid var(--v0-divider);
    border-radius: 0.5rem;
  }

  /* Focus indicator for keyboard scrolling (inset to avoid clipping by overflow-hidden parent) */
  .shiki:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--v0-primary);
  }

  /* The window still scrolls (wheel, trackpad, keyboard). The bar stays
     hidden. A fade on each edge shows that the file continues that way:
     the top fade once the start has scrolled away, the bottom fade until
     the end arrives. */
  .docs-code-window {
    --docs-code-progress: 0;
    scrollbar-width: none;
  }

  .docs-code-window::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }

  /* Painted edge veil shared with the nav drawer (.docs-edge-fade). Color is
     the code surface (--v0-pre, surface-tint on dark) so the scrim matches the
     block instead of clearing to whatever sits behind it. */
  .docs-code-window::before,
  .docs-code-window::after,
  .docs-edge-fade::before,
  .docs-edge-fade::after {
    content: '';
    position: sticky;
    z-index: 2;
    display: block;
    height: 2.5rem;
    pointer-events: none;
  }

  /* A wide line scrolls the code window on x. Pin the veil to the inline start
     so it stays across the visible pane instead of sliding off with the line. */
  .docs-code-window::before,
  .docs-code-window::after {
    inset-inline-start: 0;
    width: 100%;
  }

  .docs-code-window::before,
  .docs-edge-fade::before {
    top: 0;
    margin-bottom: -2.5rem;
    background: linear-gradient(var(--docs-edge-fade, var(--docs-code-window-fade, var(--v0-pre, var(--v0-surface)))), transparent);
    opacity: var(--docs-edge-fade-top, 0);
  }

  .docs-code-window::after,
  .docs-edge-fade::after {
    bottom: 0;
    margin-top: -2.5rem;
    background: linear-gradient(transparent, var(--docs-edge-fade, var(--docs-code-window-fade, var(--v0-pre, var(--v0-surface)))));
    opacity: var(--docs-edge-fade-bottom, 0);
  }

  /* --docs-code-progress is written on scroll (0 at the top, 1 at the bottom).
     A scroll-timeline animation here makes Chrome drop a single wheel detent
     while the fade is held at either end. The ramp matches that timeline: the
     top fade strengthens over the first 22% of the range, the bottom fade
     lets go over the last 22%. Opacity only — a translate on these sticky
     pseudos shifts the bottom veil off the last lines as the end arrives.
     --active means the window overflows vertically. A wide line must not add
     it: progress stays 0, and the bottom veil would cover lines in view.
     An explicit --docs-edge-fade-top/bottom (the nav drawer) wins over the ramp. */
  .docs-code-window--active::before {
    opacity: var(--docs-edge-fade-top, clamp(0, calc(var(--docs-code-progress) / 0.22), 1));
  }

  .docs-code-window--active::after {
    opacity: var(--docs-edge-fade-bottom, clamp(0, calc((1 - var(--docs-code-progress)) / 0.22), 1));
  }

  .docs-code-window:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--v0-primary);
  }
</style>
